import type { CaseLibraryExamDto, CaseLibraryRowDto } from '../dto/case.dto';

import { genderDisplayText } from '@/shared/enums/gender';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function pickName(value: unknown): string {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  if (!isRecord(value)) return '';
  for (const key of ['name', 'title', 'label']) {
    const field = value[key];
    if (typeof field === 'string' && field.trim()) return field.trim();
    if (typeof field === 'number') return String(field);
  }
  return '';
}

function asString(value: unknown): string {
  return String(value ?? '').trim();
}

function asBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  const normalized = asString(value).toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
}

function asNumber(value: unknown): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function firstNonEmpty(values: unknown[]): string {
  for (const value of values) {
    const text = pickName(value);
    if (text) return text;
  }
  return '';
}

function extractArchive(row: Record<string, unknown>): Record<string, unknown> | null {
  const raw =
    row.extend ??
    row.case_maindata_archive ??
    row.caseMaindataArchive ??
    row.archive ??
    row.case_archive ??
    row.caseMaindataArchiveRecord;
  const archive = Array.isArray(raw) ? raw[0] : raw;
  return isRecord(archive) ? archive : null;
}

function joinText(parts: string[], separator: string): string {
  return parts.map(part => part.trim()).filter(Boolean).join(separator);
}

function chipByModality(modality?: unknown) {
  const m = asString(modality).toUpperCase();
  if (m === 'MR') {
    return {
      type: 'MR',
      typeBg: 'bg-purple-900/20',
      typeText: 'text-purple-400',
      typeBorder: 'border-purple-500/30',
    };
  }
  if (m === 'XR' || m === 'CR' || m === 'DR') {
    return {
      type: m || 'XR',
      typeBg: 'bg-amber-900/20',
      typeText: 'text-amber-400',
      typeBorder: 'border-amber-500/30',
    };
  }
  return {
    type: m || 'CT',
    typeBg: 'bg-blue-900/20',
    typeText: 'text-blue-400',
    typeBorder: 'border-blue-500/30',
  };
}

function toExam(row: unknown, index: number): CaseLibraryExamDto | null {
  if (!isRecord(row)) return null;
  const chip = chipByModality(row.modality ?? row.type);
  const id = asString(row.id) || `exam-${index}`;
  const name = firstNonEmpty([
    row.name,
    row.title,
    row.description,
    row.storage_imaging_name,
    row.series_description,
  ]) || `影像资料 ${index + 1}`;
  return {
    id,
    type: chip.type,
    typeBg: chip.typeBg,
    typeText: chip.typeText,
    typeBorder: chip.typeBorder,
    name,
    bodyLocation: firstNonEmpty([row.bodyLocation, row.body_location, row.bodypart]),
    shotAt: firstNonEmpty([row.shotAt, row.shot_at, row.study_date, row.studyDate]),
    device: firstNonEmpty([row.device, row.device_name, row.manufacturer, row.station_name]),
    createdAt: asString(row.createdAt ?? row.created_at),
  };
}

function extractExams(row: Record<string, unknown>, archive: Record<string, unknown> | null): CaseLibraryExamDto[] {
  const raw =
    archive?.exams ??
    archive?.imagings ??
    archive?.images ??
    row.exams ??
    row.imagings ??
    row.imaging;
  const list = Array.isArray(raw) ? raw : [];
  return list
    .map((item, index) => toExam(item, index))
    .filter((item): item is CaseLibraryExamDto => item != null);
}

function extractFactors(row: Record<string, unknown>): string[] {
  const list = Array.isArray(row.factors) ? row.factors : [];
  return list
    .map(item => isRecord(item) ? asString(item.name) : '')
    .filter(Boolean);
}

function caseTitle(patientName: string, gender: string, age: string, fallback: string): string {
  if (!patientName) return fallback;
  const parts: string[] = [];
  if (gender) parts.push(gender);
  if (age) parts.push(`${age}岁`);
  const right = parts.join('/');
  return right ? `${patientName} - ${right}` : patientName;
}

function buildCaseLibraryRow(row: Record<string, unknown>): CaseLibraryRowDto {
  const archive = extractArchive(row);
  const exams = extractExams(row, archive);
  const patientName = firstNonEmpty([archive?.name, archive?.patientName, archive?.patient_name]);
  const gender = genderDisplayText(firstNonEmpty([archive?.gender, archive?.patientGender, archive?.patient_gender]));
  const age = firstNonEmpty([archive?.age, archive?.patientAge, archive?.patient_age]);
  const hospital = firstNonEmpty([row.hospital]);
  const doctor = firstNonEmpty([row.doctor]);
  const center = firstNonEmpty([row.site]);
  const factors = extractFactors(row);
  return {
    id: asString(row.id),
    name: asString(row.name),
    patientName,
    gender,
    age,
    hospital,
    department: firstNonEmpty([row.hospital_department]),
    doctor,
    bodyPart: firstNonEmpty([archive?.bodypart, archive?.bodyPart, archive?.medical_advice_bodypart]),
    bodyPartOrder: firstNonEmpty([archive?.medical_advice_bodypart, archive?.bodyPartOrder, archive?.body_part_order]),
    indication: firstNonEmpty([archive?.indication, archive?.ga_indication, archive?.indicationInfo]),
    treatmentPlan: firstNonEmpty([archive?.treatment_plan, archive?.ga_treatment_plan, archive?.treatmentPlan]),
    isHospitalized: asBoolean(
      archive?.isHospitalized ??
        archive?.is_hospitalized ??
        archive?.hospitalized ??
        archive?.inHospital,
    ),
    center,
    createdAt: asString(row.createdAt ?? row.created_at),
    updatedAt: asString(row.updatedAt ?? row.updated_at),
    examCount: asNumber(archive?.examCount ?? archive?.exam_count ?? row.examCount ?? exams.length),
    exams,
    rating: asNumber(archive?.rating),
    remark: firstNonEmpty([archive?.remark, archive?.description, archive?.desc]),
    title: caseTitle(patientName, gender, age, asString(row.name)),
    hospitalDoctorText: joinText([hospital, doctor], '/'),
    orderCenter: center,
    factorName: firstNonEmpty([archive?.factor, archive?.ga_factor, archive?.factorName, archive?.factor_name, row.factor, row.ga_factor]),
    factors,
  };
}

export class CaseLibraryMapper {
  static toRow(row: unknown): CaseLibraryRowDto | null {
    if (!isRecord(row)) return null;
    const mapped = buildCaseLibraryRow(row);
    return mapped.id ? mapped : null;
  }

  static toRowList(rows: unknown[]): CaseLibraryRowDto[] {
    return (Array.isArray(rows) ? rows : [])
      .map(row => CaseLibraryMapper.toRow(row))
      .filter((row): row is CaseLibraryRowDto => row != null);
  }
}
